import { Link } from 'components/core-ui/Link'
import s from '../partners.modules.scss'
import Table from 'components/core-ui/Table/Table'
import { useEffect, useMemo, useState } from 'react'
import { useNotification } from 'hooks'
import { useNavigate } from 'react-router-dom'
import { getColumns, getFilters } from './helpers'
import { useLocationTypesQuery, useStoreQuery } from '../api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import {
  Assignment,
  BusinessOutlined,
  Groups,
  GroupsOutlined,
  PersonOutlineOutlined
} from '@mui/icons-material'
import { AddActionIcon, DeleteActionIcon, EditActionIcon, ViewActionIcon } from 'icons'
import { makeCoreGetRequest } from 'utils/api'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { URLS } from 'components/iam/constants'
import { CircularProgress, useMediaQuery } from '@mui/material'
import { NewStoreModal } from './NewStoreModal'
import { DeleteConfirmationModal } from 'components/core-ui/DeleteConfirmationModal'

const Stores = () => {
  // TODO define interfaces for table items
  const [tableItems, setTableItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [locationTypesOptions, setLocationTypesOptions] = useState([])
  const [selectedRow, setSelectedRow] = useState<any>()
  const [isActive, setIsActive] = useState<boolean>(true)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)

  const { showNotification } = useNotification()

  const navigate = useNavigate()

  const openNewModal = (): void => setIsNewModalOpen(true)
  const closeNewModal = (): void => setIsNewModalOpen(false)

  const openEditModal = (): void => setIsEditModalOpen(true)
  const closeEditModal = (): void => setIsEditModalOpen(false)

  const openDeleteModal = (): void => setIsDeleteModalOpen(true)
  const closeDeleteModal = (): void => setIsDeleteModalOpen(false)

  const {
    data: locationTypesData,
    isLoading: isLocationTypesLoading,
    refetch: refetchLocationTypes
  } = useLocationTypesQuery(showNotification, '/iam/external-partners/location-types')

  useEffect(() => {
    if (locationTypesData) {
      setLocationTypesOptions(
        locationTypesData.map(locationType => ({
          label: locationType.name,
          value: locationType.id
        }))
      )
    }
  }, [locationTypesData])

  const columns = useMemo(() => getColumns(), [])

  const filters = useMemo(() => getFilters(locationTypesOptions), [locationTypesOptions])

  const isXs = useMediaQuery('(max-width:624px)')

  const toolbarActions = useMemo(
    () => [
      {
        label: 'Partners',
        icon: <Groups width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_PARTNERS_MANAGEMENT)
      },
      {
        label: 'Managers',
        icon: <PersonOutlineOutlined width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_MANAGERS)
      },
      {
        label: 'Delegates',
        icon: <GroupsOutlined width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_DELEGATES)
      },
      {
        label: 'Assignments',
        icon: <Assignment width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_ASSIGNMENTS)
      },
      {
        label: 'Legal entities',
        icon: <BusinessOutlined width={18} style={{ color: '#0970E6' }} />,
        onClick: () => navigate(URLS.IAM_LEGAL_ENTITIES)
      },
      {
        label: 'Add Store',
        icon: <AddActionIcon />,
        onClick: () => openNewModal()
      }
    ],
    []
  )

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters, searchText }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/iam/external-partners/stores',
          tableFilters,
          searchText,
          searchFields: ['externalIdList.value', 'legalEntity.name', 'name'],
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `There was an error fetching stores. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const {
    data: store,
    isLoading: isStoreLoading,
    refetch
  } = useStoreQuery(showNotification, Number(selectedRow), shouldRefreshData, isEditModalOpen)

  const selectRow = (rowId: number, action: string): void => {
    setSelectedRow(rowId)

    if (action === 'edit') {
      openEditModal()
    } else if (action === 'delete') {
      openDeleteModal()
    }
  }

  return (
    <div className={s.mainContainer}>
      <div className={s.breadcrumbsContainer}>
        <Link href="/iam/partners" className={s.breadcrumbsLink}>
          External Partners Management
        </Link>
        <span className={s.breadcrumbsSeparator}>|</span>
        <span className={s.breadcrumbsCurrent}>Stores</span>
      </div>
      <NewStoreModal
        isModalOpen={isNewModalOpen}
        closeModal={closeNewModal}
        setShouldRefreshData={setShouldRefreshData}
      />
      <NewStoreModal
        isModalOpen={isEditModalOpen}
        closeModal={closeEditModal}
        setShouldRefreshData={setShouldRefreshData}
        editItem={store}
      />
      <DeleteConfirmationModal
        isModalOpen={isDeleteModalOpen}
        actionUrl="/iam/external-partners/stores/:id"
        content="Are you sure you want to remove this store? This action cannot be undone."
        closeModal={closeDeleteModal}
        itemId={selectedRow}
        setShouldRefreshData={() => setShouldRefreshData(true)}
        succesMessage="Store deleted successfully."
        title="Remove store"
        errorMessage="Failed to delete store."
      />
      <Table
        hideCheckbox
        hasSearchInput
        orderBy="id"
        order="desc"
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        shouldResetPage={shouldResetPage}
        setShouldResetPage={setShouldResetPage}
        rowActions={[
          {
            label: 'Details',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#0970E6',
            desktopIcon: <ViewActionIcon />,
            onClick: (_e: React.MouseEvent<HTMLElement>, rowId: number) =>
              navigate(`/iam/externalPartners/stores/${rowId}`)
          },
          {
            label: 'Edit',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#16a085',
            desktopIcon: <EditActionIcon />,
            onClick: (_e, rowId) => selectRow(+rowId, 'edit')
          },
          {
            label: 'Sterge',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#d9534f',
            desktopIcon: <DeleteActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => selectRow(+rowId, 'delete'),
            hideCondition: { field: 'isHandedOver', value: true }
          }
        ]}
        rowActionsWidth="80px"
        desktopRowActionsWidth="125px"
        toolbarActions={toolbarActions}
        filters={filters}
      />
    </div>
  )
}

export default Stores
